import styles from './AdvancedImageUpload.module.scss';

import classnames from 'classnames';
import classNames from 'classnames';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import { t } from '@/utility/localization';

import ByDefaultGallery from '@/components/common/Form/AdvancedImageUpload/ByDefaultGallery';
import ByUserUpload from '@/components/common/Form/AdvancedImageUpload/ByUserUpload';
import Icon from '@/components/npl/Icon';
import NPLButton from '@/components/npl/NPLButton';

import StatusTabs from '@/pages/portal/components/StatusTabs';

const AdvancedImageUpload = ({
  uploadHint = t('recommended-size-1920x1080-16-9-and-less-than-10mb'),
  defaultImages = [],
  value,
  previewSize = {
    width: 1920 / 10 + 'px',
    height: 1080 / 10 + 'px'
  },
  onInput,
  entityId = null,
  useCase,
  renderCustomUI = null,
  buttonOnImage = false,
  withOverlay = false,
  isOverlayHoverable,
  onMouseOverImage = () => {},
  onMouseOutImage = () => {},
  showEditIcon = false,
  removeButtonLabel = '',
  overlayTitle = t('edit')
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [activeTab, setActiveTab] = useState('upload');

  let tabs = [];
  if (defaultImages && defaultImages.length > 0) {
    tabs = [
      { id: 'upload', label: t('upload') },
      { id: 'gallery', label: t('gallery') }
    ];
  }

  const pickInput = (image) => {
    setIsModalVisible(false);
    if (onInput) onInput(image);
  };

  const handleCloakClick = (e) => {
    // Children of the cloak also triggers the event, we need to check if the clicked element
    // is the cloak itself
    e.currentTarget === e.target && setIsModalVisible(false);
  };

  const renderUI = () => {
    if (renderCustomUI)
      return renderCustomUI({ openModal: () => setIsModalVisible(true) });
    return (
      <div className={buttonOnImage && 'relative'}>
        <div
          className={classNames(
            'relative mb-8 flex items-center justify-center rounded-12 ',
            {
              'bg-neutral-90': !value
            }
          )}
          style={{
            width: previewSize.width,
            height: previewSize.height
          }}
          onMouseEnter={onMouseOverImage}
          onMouseLeave={onMouseOutImage}
          onBlur={() => {}}>
          {withOverlay && (
            <div
              className={classNames(
                'absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center rounded-12 bg-npl-transparent-black-50',
                {
                  'opacity-0 transition-all hover:opacity-[1]':
                    isOverlayHoverable
                }
              )}
              onClick={() => {
                showEditIcon && setIsModalVisible(true);
              }}
              role="button"
              tabIndex={0}>
              <p
                className={classNames(
                  'text-npl-label-md font-medium text-npl-text-primary-on-dark',
                  {
                    hidden: !showEditIcon
                  }
                )}>
                {overlayTitle}
              </p>
            </div>
          )}

          <img
            src={value}
            alt=""
            className="h-full w-full rounded-12 object-cover"
          />

          {showEditIcon && (
            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsModalVisible(true)}
              className="absolute bottom-[-8px] right-[-8px] grid h-32 w-32 place-content-center rounded-full border-2 border-white-default bg-npl-neutral-dark-solid-3 hover:cursor-pointer">
              <Icon
                name="pencil-01"
                fill="#F5F5F5"
                width={16}
                height={16}
              />
            </div>
          )}

          {!value && <div className="absolute">{t('no-image-set')}</div>}
        </div>
      </div>
    );
  };

  //this component is not allowed to be rendered on the server
  if (typeof window === 'undefined') {
    return;
  }

  return (
    <div>
      {renderUI()}
      {isModalVisible
        ? createPortal(
            // we really don't want this to be tabbable
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              className={classnames(
                'fixed left-0 top-0 flex h-full w-full items-center justify-center bg-opacity-50 z-over-intercom',
                'bg-dark-75'
              )}
              onClick={handleCloakClick}>
              <div className={styles.animationPopIn}>
                <div className="relative flex w-[540px] flex-col rounded-12 bg-white-default p-24 shadow-2xl">
                  {tabs.length > 1 && (
                    <StatusTabs
                      tabs={tabs}
                      activeTabId={activeTab}
                      onClick={(tabId) => setActiveTab(tabId)}
                    />
                  )}
                  <div className="absolute right-16 top-16">
                    <NPLButton
                      onClick={() => setIsModalVisible(false)}
                      leadIcon="x-close"
                      size="md"
                      rounded
                      hierarchy="plain"
                    />
                  </div>

                  {activeTab === 'gallery' && (
                    <div className="mt-24">
                      <ByDefaultGallery
                        defaultImages={defaultImages}
                        onPickImage={pickInput}
                      />
                    </div>
                  )}

                  {activeTab === 'upload' && (
                    <div className="mt-24">
                      <ByUserUpload
                        entityId={entityId}
                        useCase={useCase}
                        onPickImage={pickInput}
                        uploadHint={uploadHint}
                        removeButtonLabel={removeButtonLabel}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default AdvancedImageUpload;
